import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { firstValueFrom, map, Observable, take } from 'rxjs';

import {
  IRemoteSettings,
  SettingsService,
} from '@app/services/settings.service';
import {
  LanguageSelection,
  LanguageService,
} from '@app/services/language.service';
import { GuestSessionService } from '@app/services/guest-session.service';
import { LanguageSettingsService } from '@app/services/language-settings.service';
import { TtsAudioService } from '@app/services/tts-audio.service';

import { Language } from '@app/shared/models/app.model';

@Component({
  selector: 'app-language-setting-dialog',
  templateUrl: './language-setting-dialog.component.html',
  styleUrls: ['./language-setting-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSettingDialogComponent implements OnInit {
  public updateMode: 'all' | 'each' = 'all';

  public languages: Language[];
  public verifiedLanguages: Language[];
  public unverifiedLanguages: Language[];
  public currentLanguage$: Observable<LanguageSelection>;
  public kit: Observable<IRemoteSettings>;
  public isMicrophoneKit$: Observable<boolean>;
  public localLanguageOut$: Observable<string>;
  public localLanguageIn$: Observable<string>;
  public pendingLanguageIn: Map<number, Language> = new Map();
  public pendingLanguageOut: Language | null = null;
  public pendingLanguageOutPerMic: Map<number, Language> = new Map();

  private qrUserService = inject(GuestSessionService);
  private ttsAudio = inject(TtsAudioService);
  public isQRAuthMode = this.qrUserService.isGuestUser();

  public translationsDisabled: Signal<boolean> = computed(() =>
    this.isQRAuthMode && this.qrUserService.guestTranslationsEnabled() === false
  );

  constructor(
    public languageService: LanguageService,
    private settings: SettingsService,
    private dialogRef: MatDialogRef<LanguageSettingDialogComponent>,
    public languageSettings: LanguageSettingsService
  ) {
    this.localLanguageOut$ = settings.localLanguageOut$;
    this.localLanguageIn$ = settings.localLanguageIn$;
    this.currentLanguage$ = languageSettings.getCurrentLanguage$();
    this.kit = this.settings.getRemoteSetSettings();
    this.isMicrophoneKit$ = this.kit.pipe(
      map((k) => (k?.microphones?.length ?? 0) > 1)
    );
  }

  ngOnInit() {
    const languages = this.offeredLanguages();
    this.languages = languages;
    this.verifiedLanguages = languages.filter((lang) => lang.isVerified);
    this.unverifiedLanguages = languages.filter((lang) => !lang.isVerified);
  }

  /**
   * The languages this dialog offers.
   *
   * A guest who is listening to read-along must not be offered a region the
   * proxy cannot speak, because it refuses to subscribe to those: picking one
   * would leave them with captions and silence. This is the same rule the
   * initial picker applies, and it has to be repeated here because changing
   * language later goes through this dialog rather than that one.
   *
   * Availability comes from `tts_available` on the proxy's supported-languages
   * push, matched by code onto the ordinary list rather than replacing it, so
   * the per-device licence restrictions inside getAllLanguages() still apply.
   * With read-along off the list is untouched: translation works for every
   * locale regardless of which voices exist.
   */
  private offeredLanguages(): Language[] {
    const languages = this.languageSettings.getAllLanguages();
    if (!this.isQRAuthMode || !this.ttsAudio.enabled()) return languages;

    const availability = new Map(
      this.qrUserService
        .supportedLanguages()
        .map((lang) => [lang.languageCode, lang.ttsAvailable])
    );
    return languages.filter(
      (lang) => availability.get(lang.languageCode) !== false
    );
  }

  public languageInChanged(micId: number, language: Language) {
    if (this.updateMode === 'all') {
      // Clear per-mic and set for all (use -1 as key for "all")
      this.pendingLanguageIn.clear();
      this.pendingLanguageIn.set(-1, language);
    } else {
      this.pendingLanguageIn.set(micId, language);
    }
  }

  public languageOutChanged(micId: number, language: Language) {
    if (this.updateMode === 'all') {
      this.pendingLanguageOut = language;
      this.pendingLanguageOutPerMic.clear();
    } else {
      this.pendingLanguageOutPerMic.set(micId, language);
    }
  }

  public async save() {
    const kitSettings = await firstValueFrom(this.kit.pipe(take(1)));
    const allMicIds = kitSettings.microphones.map((x) => x.mic_id);

    this.applyPendingLanguageInChanges(allMicIds);
    this.applyPendingLanguageOutChanges(allMicIds);

    this.dialogRef.close();
  }

  private applyPendingLanguageInChanges(allMicIds: number[]) {
    for (const [micId, language] of this.pendingLanguageIn) {
      const isAllMode = micId === -1;
      const microphones = isAllMode ? allMicIds : [micId];

      this.languageSettings
        .updateMicrophoneLanguage(microphones[0], language, 'in', isAllMode, microphones)
        .pipe(take(1))
        .subscribe();
    }
  }

  private applyPendingLanguageOutChanges(allMicIds: number[]) {
    // All mode
    if (this.pendingLanguageOut) {
      this.applyLanguageOutChange(this.pendingLanguageOut, allMicIds);
      return;
    }

    // Per-mic mode
    for (const [micId, language] of this.pendingLanguageOutPerMic) {
      this.applyLanguageOutChange(language, [micId]);
    }
  }

  private applyLanguageOutChange(language: Language, micIds: number[]) {
    const isAllMode = micIds.length > 1;
    this.languageSettings.selectTranslationLanguage(language);

    if (this.isQRAuthMode) {
      this.qrUserService.saveLanguagePreference(language.languageCode);

      // Soundshape QR: Use websocket
      if (this.qrUserService.isSoundshapeQR()) {
        const updateMode = isAllMode ? 'all' : 'each';
        const micId = isAllMode ? undefined : micIds[0];
        this.qrUserService.sendTargetLanguageUpdate(language.languageCode, micId, updateMode)
          .pipe(take(1))
          .subscribe();
        return;
      }

      // Microphonekit QR: Use API (same as non-QR users)
      this.languageSettings
        .updateMicrophoneLanguage(micIds[0], language, 'out', isAllMode, micIds)
        .pipe(take(1))
        .subscribe();
      return;
    }

    // Non-QR users
    this.languageSettings
      .updateMicrophoneLanguage(micIds[0], language, 'out', isAllMode, micIds)
      .pipe(take(1))
      .subscribe();
  }
}
